import { PostHog } from "posthog-js/react";
import { UserWithPermissions } from "types";
import { envConfig } from "config";
import { isEnvProd } from "utils/environment";
import posthog from "posthog-js";

// Posthog events that do not require special meta properties.
type BasicPosthogEventType = "spa_login:success" | "spa_logout:success";

type PosthogEvent =
  | { type: BasicPosthogEventType }
  | {
      type: "spa_login:attempt";
      properties: { username: string; method: "password" };
    }
  | {
      type: "spa_login:failure";
      properties: { username: string; error: string | null };
    };

const getAccount = (user: UserWithPermissions) => ({
  accountId: user.account.accountId,
  name: user.account.name,
  accountType: user.account.accountType,
  updatedTime: user.account.updatedTime,
});

/**
 * Initializes Posthog.
 * This is called on app startup to initialize Posthog.
 *
 * NOTE: We opt out of event capturing in non-production environments.
 * We originally followed this guide: https://posthog.com/tutorials/multiple-environments#conditional-initialization
 * But `ph.set_config` was removed from the types around v1.324.1.
 * While researching that change I found that there is top level config for: `disable_session_recording` and `opt_out_capturing_by_default`
 * https://posthog.com/docs/session-replay/how-to-control-which-sessions-you-record
 * https://posthog.com/docs/libraries/js#opt-out-of-data-capture
 *
 * The above doc also recommends passing a fake key/token, however,
 * this, however, results in a 401 response while initializing the Posthog client.
 */
export const initializePosthog = (): typeof posthog => {
  posthog.init(envConfig.posthogKey, {
    api_host: envConfig.posthogHost,
    person_profiles: "identified_only",
    capture_pageview: false,
    disable_session_recording: !isEnvProd(),
    opt_out_capturing_by_default: !isEnvProd(),
  });

  return posthog;
};

/**
 * Bootstraps Posthog with user data.
 */
export const bootPosthog = (posthog: PostHog, user: UserWithPermissions) => {
  const account = getAccount(user);
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");

  posthog.identify(user.userId.toString(), {
    name,
    email: user.userInfo.email,
    isSysAdmin: user.isSysAdmin,
    isInternal: user.isInternal,
    isImpersonated: user.isImpersonated,
    dateRegistered: user.userInfo.dateRegistered,
    account: account,
  });

  // posthog groups are an upgraded subscription feature
  posthog.group("company", account.accountId.toString(), account);
};

/**
 * Expected to be called when the user logs out.
 * https://posthog.com/docs/libraries/js#reset-after-logout
 */
export const logoutPosthogUser = () => {
  posthog.reset();
};

/**
 * Submits an event to Posthog.
 *
 * @param event The event to be submitted to Posthog.
 */
export const submitPosthogEvent = (event: PosthogEvent) => {
  posthog.capture(event.type, "properties" in event ? event.properties : {});
};
